import {
    MakeLogicType,
    actions,
    afterMount,
    connect,
    kea,
    listeners,
    path,
    reducers,
    selectors,
    sharedListeners,
} from 'kea'
import type { BreakPointFunction } from 'kea'
import { loaders } from 'kea-loaders'
import { router } from 'kea-router'

import { FEATURE_FLAGS } from 'lib/constants'
import { featureFlagLogic } from 'lib/logic/featureFlagLogic'
import posthog from 'lib/posthog-typed'
import { teamLogic } from 'scenes/teamLogic'
import { urls } from 'scenes/urls'

import type { FeatureFlagsSet } from '../../../../frontend/src/lib/logic/featureFlagLogic'
import { visionScannersList } from '../generated/api'
import { markGoalDraftIntent } from '../replay_scanners/goalDraftIntent'
import { getReplayVisionEditDisabledReason } from '../utils/accessControl'

export const ANALYSIS_NUDGE_THRESHOLD = 5

/** How long a shown-but-ignored nudge stays quiet. Dismissal and submission suppress forever;
 * ignoring is softer, so it re-pitches eventually rather than every qualifying session. */
export const IGNORED_NUDGE_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface analysisNudgeLogicValues {
    featureFlags: FeatureFlagsSet // featureFlagLogic
    analyzedRecordingIds: string[]
    cooldownActive: boolean
    goalInput: string
    lastShownAt: number | null
    nudgeVisible: boolean
    suppressed: boolean
    teamHasScanners: boolean | null
    teamHasScannersLoading: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface analysisNudgeLogicActions {
    checkTeamScanners: () => any
    checkTeamScannersFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    checkTeamScannersSuccess: (
        teamHasScanners: boolean | null,
        payload?: any
    ) => {
        teamHasScanners: boolean | null
        payload?: any
    }
    dismissNudge: () => {
        value: true
    }
    nudgeShown: () => {
        shownAt: number
    }
    recordingAnalyzed: (sessionRecordingId: string) => {
        sessionRecordingId: string
    }
    setCooldownActive: (active: boolean) => {
        active: boolean
    }
    setGoalInput: (goal: string) => {
        goal: string
    }
    submitGoal: (goal: string) => {
        goal: string
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface analysisNudgeLogicMeta {
    sharedListeners: {
        reportShownIfVisible: (
            payload: any,
            breakpoint: BreakPointFunction,
            action: {
                type: string
                payload: any
            },
            previousState: any
        ) => void | Promise<void>
    }
    __keaTypeGenInternalSelectorTypes: {
        nudgeVisible: (
            analyzedRecordingIds: string[],
            suppressed: boolean,
            cooldownActive: boolean,
            teamHasScanners: boolean | null,
            featureFlags: FeatureFlagsSet
        ) => boolean
    }
}

export type analysisNudgeLogicType = MakeLogicType<
    analysisNudgeLogicValues,
    analysisNudgeLogicActions,
    Record<string, any>,
    analysisNudgeLogicMeta
>

/** After a user analyzes a few recordings in one browser session, offers to turn what they're
 * looking for into a Replay Vision scanner via the AI goal-draft flow on the creation wizard. */
export const analysisNudgeLogic = kea<analysisNudgeLogicType>([
    path(['products', 'replay_vision', 'frontend', 'logics', 'analysisNudgeLogic']),
    connect(() => ({ values: [featureFlagLogic, ['featureFlags']] })),
    actions({
        recordingAnalyzed: (sessionRecordingId: string) => ({ sessionRecordingId }),
        dismissNudge: true,
        nudgeShown: () => ({ shownAt: Date.now() }),
        setCooldownActive: (active: boolean) => ({ active }),
        setGoalInput: (goal: string) => ({ goal }),
        submitGoal: (goal: string) => ({ goal }),
    }),
    loaders({
        // A team already running scanners doesn't need the pitch. Null means "not checked yet"
        // and keeps the nudge hidden, so an API failure can never pitch such a team.
        teamHasScanners: [
            null as boolean | null,
            {
                checkTeamScanners: async () => {
                    const teamId = teamLogic.findMounted()?.values.currentTeamId
                    if (!teamId) {
                        return null
                    }
                    try {
                        const response = await visionScannersList(String(teamId), { limit: 1 })
                        return (response.results ?? []).length > 0
                    } catch {
                        return null
                    }
                },
            },
        ],
    }),
    reducers({
        // Unique ids so re-watching one recording doesn't count as hunting through several.
        analyzedRecordingIds: [
            [] as string[],
            {
                recordingAnalyzed: (state, { sessionRecordingId }) =>
                    state.includes(sessionRecordingId) ? state : [...state, sessionRecordingId],
            },
        ],
        // Persisted so neither a dismissal nor a submission ever nags the same browser again.
        suppressed: [
            false,
            { persist: true },
            {
                dismissNudge: () => true,
                submitGoal: () => true,
            },
        ],
        // Persisted so a shown-but-ignored nudge counts against the cooldown across sessions.
        lastShownAt: [
            null as number | null,
            { persist: true },
            {
                nudgeShown: (_, { shownAt }) => shownAt,
            },
        ],
        // The cooldown is evaluated once at mount: recording a sighting mid-session must not
        // hide the card the user is currently reading.
        cooldownActive: [
            false,
            {
                setCooldownActive: (_, { active }) => active,
            },
        ],
        goalInput: ['', { setGoalInput: (_, { goal }) => goal }],
    }),
    selectors({
        nudgeVisible: [
            (s) => [s.analyzedRecordingIds, s.suppressed, s.cooldownActive, s.teamHasScanners, s.featureFlags],
            (
                analyzedRecordingIds: string[],
                suppressed: boolean,
                cooldownActive: boolean,
                teamHasScanners: boolean | null,
                featureFlags: Record<string, boolean | string | undefined>
            ): boolean => {
                // Reading the flags proxy reports exposure, so the read stays behind the other
                // guards: exposure has to cover exactly the users who qualified for the nudge.
                if (suppressed || cooldownActive || analyzedRecordingIds.length < ANALYSIS_NUDGE_THRESHOLD) {
                    return false
                }
                // Strict false so both "team already runs scanners" and "not known yet" hide it.
                if (teamHasScanners !== false) {
                    return false
                }
                return !!featureFlags[FEATURE_FLAGS.REPLAY_VISION_ANALYSIS_NUDGE]
            },
        ],
    }),
    sharedListeners(({ actions, cache, values }) => ({
        // Fires from both paths that can flip nudgeVisible to true: the analyzed count crossing
        // the threshold, and the async scanners check resolving after it.
        reportShownIfVisible: () => {
            // Mirrors the component's render guards so "shown" only fires when the card rendered.
            if (!values.nudgeVisible || cache.shownReported || getReplayVisionEditDisabledReason()) {
                return
            }
            cache.shownReported = true
            actions.nudgeShown()
            posthog.capture('replay vision analysis nudge shown', {
                analyzed_count: values.analyzedRecordingIds.length,
            })
        },
    })),
    listeners(({ actions, values, sharedListeners }) => ({
        recordingAnalyzed: [
            () => {
                // Checked lazily, only once a viewer qualifies, so plain replay watching never
                // fetches the scanner list.
                if (
                    values.teamHasScanners === null &&
                    !values.teamHasScannersLoading &&
                    !values.suppressed &&
                    !values.cooldownActive &&
                    values.analyzedRecordingIds.length >= ANALYSIS_NUDGE_THRESHOLD
                ) {
                    actions.checkTeamScanners()
                }
            },
            sharedListeners.reportShownIfVisible,
        ],
        checkTeamScannersSuccess: sharedListeners.reportShownIfVisible,
        dismissNudge: () => {
            posthog.capture('replay vision analysis nudge dismissed', {
                analyzed_count: values.analyzedRecordingIds.length,
            })
        },
        submitGoal: ({ goal }) => {
            posthog.capture('replay vision analysis nudge submitted', {
                analyzed_count: values.analyzedRecordingIds.length,
                goal_length: goal.length,
            })
            // The goal travels via the sessionStorage hand-off, never the URL: it is free text
            // that can contain customer emails or names, and a query param would land in
            // autocapture's $current_url, our own replay, and browser history. The wizard
            // consumes it on mount, prefills the AI box, and starts the draft, so all creation
            // gates (consent, estimate, quota) stay in force.
            markGoalDraftIntent(goal)
            router.actions.push(urls.replayVisionTemplates())
        },
    })),
    afterMount(({ actions, values }) => {
        if (values.lastShownAt !== null && Date.now() - values.lastShownAt < IGNORED_NUDGE_COOLDOWN_MS) {
            actions.setCooldownActive(true)
        }
    }),
])
